/**
 * Create Token Modal Component (Simplified)
 * Form for creating a new partner access token
 * Partner gets access to all games where they are involved via Transportation
 */

import React, { useState, useEffect } from 'react';
import { CheckCircle, AlertTriangle, ClipboardCopy } from 'lucide-react';
import { Partner } from '../../types/partners';
import {
  CreatePartnerTokenRequest,
  CreatePartnerTokenResponse,
  DisplayMode,
  DisplayModeLabels,
  AllowedUpdateFieldOptions
} from '../../types/partnerAccess';

interface CreateTokenModalProps {
  partners: Partner[];
  onClose: () => void;
  onCreate: (request: CreatePartnerTokenRequest) => Promise<CreatePartnerTokenResponse>;
}

const CreateTokenModal: React.FC<CreateTokenModalProps> = ({ partners, onClose, onCreate }) => {
  // Form state
  const [tokenName, setTokenName] = useState('');
  const [tokenDescription, setTokenDescription] = useState('');
  const [partnerId, setPartnerId] = useState<number | null>(null);
  const [displayMode, setDisplayMode] = useState<DisplayMode>(DisplayMode.Full);
  const [canUpdate, setCanUpdate] = useState(false);
  const [allowedUpdateFields, setAllowedUpdateFields] = useState<string[]>([]);
  const [expiresAt, setExpiresAt] = useState('');
  const [allowedIpAddresses, setAllowedIpAddresses] = useState('');
  const [rateLimitPerHour, setRateLimitPerHour] = useState(100);

  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [createdToken, setCreatedToken] = useState<string | null>(null);

  // Debug: Log when createdToken changes
  useEffect(() => {
    console.log('CreateTokenModal: createdToken state changed to:', createdToken);
  }, [createdToken]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!tokenName.trim()) {
      newErrors.tokenName = 'Token-Name ist erforderlich';
    }

    if (!partnerId) {
      newErrors.partnerId = 'Partner ist erforderlich';
    }

    if (canUpdate && allowedUpdateFields.length === 0) {
      newErrors.allowedUpdateFields = 'Mindestens ein Update-Feld muss ausgewählt werden';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    try {
      setIsSubmitting(true);

      const request: CreatePartnerTokenRequest = {
        partnerId: partnerId!,
        tokenName,
        tokenDescription: tokenDescription || undefined,
        displayMode,
        canUpdate,
        allowedUpdateFields: canUpdate ? allowedUpdateFields : undefined,
        expiresAt: expiresAt || undefined,
        allowedIpAddresses: allowedIpAddresses || undefined,
        rateLimitPerHour: rateLimitPerHour || undefined,
      };

      console.log('CreateTokenModal: Sending request:', request);
      const response = await onCreate(request);
      console.log('CreateTokenModal: Received response:', response);
      console.log('CreateTokenModal: Token from response:', response.token);

      // Show the plaintext token (only shown once!)
      if (response && response.token) {
        console.log('CreateTokenModal: Setting createdToken to:', response.token);
        setCreatedToken(response.token);
      } else {
        console.error('CreateTokenModal: No token in response!', response);
        setErrors({ submit: 'Fehler: Kein Token in der Antwort erhalten' });
      }
    } catch (err) {
      console.error('CreateTokenModal: Error creating token:', err);
      setErrors({ submit: 'Fehler beim Erstellen des Tokens: ' + (err as Error).message });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCopyToken = () => {
    if (createdToken) {
      navigator.clipboard.writeText(createdToken);
      alert('Token in Zwischenablage kopiert!');
    }
  };

  const handleClose = () => {
    if (createdToken) {
      const confirmed = window.confirm(
        'Der Token wird nach dem Schließen nicht mehr angezeigt. Haben Sie ihn gespeichert?'
      );
      if (!confirmed) return;
    }
    onClose();
  };

  const handleFieldToggle = (field: string) => {
    setAllowedUpdateFields(prev =>
      prev.includes(field)
        ? prev.filter(f => f !== field)
        : [...prev, field]
    );
  };

  // If token was created, show success screen
  console.log('CreateTokenModal: Rendering, createdToken =', createdToken);
  if (createdToken) {
    console.log('CreateTokenModal: Rendering SUCCESS SCREEN with token:', createdToken);
    return (
      <div style={styles.overlay}>
        <div style={styles.modal}>
          <div style={styles.header}>
            <h2 style={styles.title}><CheckCircle size={18} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 6 }} /> Token erfolgreich erstellt!</h2>
          </div>

          <div style={styles.body}>
            <div style={styles.warningBox}>
              <p style={styles.warningText}>
                <AlertTriangle size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> <strong>WICHTIG:</strong> Dieser Token wird nur einmal angezeigt!
              </p>
              <p style={styles.warningSubtext}>
                Bitte speichern Sie den Token sicher. Er kann nicht wiederhergestellt werden.
              </p>
            </div>

            <div style={styles.tokenDisplay}>
              <label style={styles.label}>API Token:</label>
              <div style={styles.tokenBox}>
                <code style={styles.tokenCode}>{createdToken}</code>
              </div>
              <button
                type="button"
                onClick={handleCopyToken}
                style={styles.copyButton}
              >
                <ClipboardCopy size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> In Zwischenablage kopieren
              </button>
            </div>

            <div style={styles.infoBox}>
              <p><strong>Token-Name:</strong> {tokenName}</p>
              <p><strong>Partner:</strong> {partners.find(p => p.id === partnerId)?.name}</p>
              <p><strong>Anzeigemodus:</strong> {DisplayModeLabels[displayMode]}</p>
              <p><strong>Kann aktualisieren:</strong> {canUpdate ? 'Ja' : 'Nein'}</p>
            </div>
          </div>

          <div style={styles.footer}>
            <button
              type="button"
              onClick={handleClose}
              style={styles.closeButton}
            >
              Schließen
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Otherwise, show the form
  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <div style={styles.header}>
          <h2 style={styles.title}>Neuen Partner-Token erstellen</h2>
          <button type="button" onClick={handleClose} style={styles.closeIcon}>×</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={styles.body}>
            {/* Token Name */}
            <div style={styles.formGroup}>
              <label style={styles.label}>
                Token-Name <span style={styles.required}>*</span>
              </label>
              <input
                type="text"
                value={tokenName}
                onChange={(e) => setTokenName(e.target.value)}
                style={errors.tokenName ? { ...styles.input, ...styles.inputError } : styles.input}
                placeholder="z.B. Lufthansa API Token"
              />
              {errors.tokenName && <span style={styles.errorText}>{errors.tokenName}</span>}
            </div>

            {/* Partner Selection */}
            <div style={styles.formGroup}>
              <label style={styles.label}>
                Partner <span style={styles.required}>*</span>
              </label>
              <select
                value={partnerId || ''}
                onChange={(e) => setPartnerId(Number(e.target.value))}
                style={errors.partnerId ? { ...styles.select, ...styles.inputError } : styles.select}
                title="Partner auswählen"
              >
                <option value="">-- Partner auswählen --</option>
                {Array.isArray(partners) && partners.map(partner => (
                  <option key={partner.id} value={partner.id}>
                    {partner.name} ({partner.partnerType})
                  </option>
                ))}
              </select>
              {errors.partnerId && <span style={styles.errorText}>{errors.partnerId}</span>}
              <p style={styles.helpText}>
                Der Partner erhält Zugriff auf alle Spiele, bei denen er in der Spieltagsplanung eingebunden ist.
              </p>
            </div>

            {/* Token Description */}
            <div style={styles.formGroup}>
              <label style={styles.label}>Beschreibung</label>
              <textarea
                value={tokenDescription}
                onChange={(e) => setTokenDescription(e.target.value)}
                style={styles.textarea}
                placeholder="Optionale Beschreibung..."
                rows={3}
              />
            </div>

            {/* Display Mode */}
            <div style={styles.formGroup}>
              <label style={styles.label}>
                Anzeigemodus <span style={styles.required}>*</span>
              </label>
              <select
                value={displayMode}
                onChange={(e) => setDisplayMode(e.target.value as DisplayMode)}
                style={styles.select}
                title="Anzeigemodus auswählen"
              >
                {Object.entries(DisplayModeLabels).map(([key, label]) => (
                  <option key={key} value={key}>
                    {label}
                  </option>
                ))}
              </select>
            </div>

            {/* Update Permissions */}
            <div style={styles.formGroup}>
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={canUpdate}
                  onChange={(e) => setCanUpdate(e.target.checked)}
                  style={styles.checkbox}
                />
                Daten aktualisieren erlauben
              </label>
            </div>

            {canUpdate && (
              <div style={styles.formGroup}>
                <label style={styles.label}>Erlaubte Update-Felder</label>
                <div style={styles.checkboxGroup}>
                  {AllowedUpdateFieldOptions.map(field => (
                    <label key={field.value} style={styles.checkboxLabel}>
                      <input
                        type="checkbox"
                        checked={allowedUpdateFields.includes(field.value)}
                        onChange={() => handleFieldToggle(field.value)}
                        style={styles.checkbox}
                      />
                      {field.label}
                    </label>
                  ))}
                </div>
                {errors.allowedUpdateFields && (
                  <span style={styles.errorText}>{errors.allowedUpdateFields}</span>
                )}
              </div>
            )}

            {/* Expiration Date */}
            <div style={styles.formGroup}>
              <label style={styles.label}>Ablaufdatum (optional)</label>
              <input
                type="datetime-local"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
                style={styles.input}
              />
            </div>

            {/* IP Whitelist */}
            <div style={styles.formGroup}>
              <label style={styles.label}>IP-Whitelist (optional)</label>
              <input
                type="text"
                value={allowedIpAddresses}
                onChange={(e) => setAllowedIpAddresses(e.target.value)}
                style={styles.input}
                placeholder="z.B. 192.168.1.1, 10.0.0.0/24"
              />
            </div>

            {/* Rate Limit */}
            <div style={styles.formGroup}>
              <label style={styles.label}>Rate Limit (Anfragen pro Stunde)</label>
              <input
                type="number"
                value={rateLimitPerHour}
                onChange={(e) => setRateLimitPerHour(Number(e.target.value))}
                style={styles.input}
                min={1}
                max={10000}
              />
            </div>

            {errors.submit && (
              <div style={styles.errorBox}>
                {errors.submit}
              </div>
            )}
          </div>

          <div style={styles.footer}>
            <button
              type="button"
              onClick={handleClose}
              style={styles.cancelButton}
              disabled={isSubmitting}
            >
              Abbrechen
            </button>
            <button
              type="submit"
              style={styles.submitButton}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Erstelle...' : 'Token erstellen'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: '#fff',
    borderRadius: '8px',
    width: '90%',
    maxWidth: '600px',
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
  },
  header: {
    padding: '20px',
    borderBottom: '1px solid #e0e0e0',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  title: {
    margin: 0,
    fontSize: '20px',
    fontWeight: 600,
  },
  closeIcon: {
    background: 'none',
    border: 'none',
    fontSize: '28px',
    cursor: 'pointer',
    color: '#666',
  },
  body: {
    padding: '20px',
  },
  formGroup: {
    marginBottom: '20px',
  },
  label: {
    display: 'block',
    marginBottom: '8px',
    fontWeight: 500,
    fontSize: '14px',
  },
  required: {
    color: '#d32f2f',
  },
  input: {
    width: '100%',
    padding: '10px',
    border: '1px solid #ccc',
    borderRadius: '4px',
    fontSize: '14px',
    boxSizing: 'border-box' as const,
  },
  select: {
    width: '100%',
    padding: '10px',
    border: '1px solid #ccc',
    borderRadius: '4px',
    fontSize: '14px',
    boxSizing: 'border-box' as const,
  },
  textarea: {
    width: '100%',
    padding: '10px',
    border: '1px solid #ccc',
    borderRadius: '4px',
    fontSize: '14px',
    boxSizing: 'border-box' as const,
    resize: 'vertical' as const,
  },
  inputError: {
    borderColor: '#d32f2f',
  },
  errorText: {
    color: '#d32f2f',
    fontSize: '12px',
    marginTop: '4px',
    display: 'block',
  },
  helpText: {
    fontSize: '12px',
    color: '#666',
    marginTop: '4px',
  },
  checkboxLabel: {
    display: 'block',
    marginBottom: '8px',
    fontSize: '14px',
  },
  checkbox: {
    marginRight: '8px',
  },
  checkboxGroup: {
    marginTop: '8px',
  },
  errorBox: {
    backgroundColor: '#ffebee',
    color: '#c62828',
    padding: '12px',
    borderRadius: '4px',
    marginTop: '16px',
  },
  footer: {
    padding: '20px',
    borderTop: '1px solid #e0e0e0',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
  },
  cancelButton: {
    padding: '10px 20px',
    border: '1px solid #ccc',
    borderRadius: '4px',
    backgroundColor: '#fff',
    cursor: 'pointer',
    fontSize: '14px',
  },
  submitButton: {
    padding: '10px 20px',
    border: 'none',
    borderRadius: '4px',
    backgroundColor: '#1976d2',
    color: '#fff',
    cursor: 'pointer',
    fontSize: '14px',
  },
  closeButton: {
    padding: '10px 20px',
    border: 'none',
    borderRadius: '4px',
    backgroundColor: '#1976d2',
    color: '#fff',
    cursor: 'pointer',
    fontSize: '14px',
  },
  warningBox: {
    backgroundColor: '#fff3cd',
    border: '1px solid #ffc107',
    borderRadius: '4px',
    padding: '16px',
    marginBottom: '20px',
  },
  warningText: {
    margin: '0 0 8px 0',
    fontSize: '14px',
    color: '#856404',
  },
  warningSubtext: {
    margin: 0,
    fontSize: '12px',
    color: '#856404',
  },
  tokenDisplay: {
    marginBottom: '20px',
  },
  tokenBox: {
    backgroundColor: '#f5f5f5',
    border: '1px solid #ccc',
    borderRadius: '4px',
    padding: '12px',
    marginTop: '8px',
    marginBottom: '12px',
    wordBreak: 'break-all' as const,
  },
  tokenCode: {
    fontFamily: 'monospace',
    fontSize: '13px',
  },
  copyButton: {
    padding: '8px 16px',
    border: '1px solid #1976d2',
    borderRadius: '4px',
    backgroundColor: '#fff',
    color: '#1976d2',
    cursor: 'pointer',
    fontSize: '14px',
  },
  infoBox: {
    backgroundColor: '#f5f5f5',
    borderRadius: '4px',
    padding: '16px',
  },
};

export default CreateTokenModal;

